import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { resolveImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { 
  User, MapPin, Briefcase, Clock, Globe, Star, 
  Phone, Video, FileText, ChevronLeft, Loader2,
  GraduationCap, Shield
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PROFESSION_LABELS = {
  psicologo: 'Psicólogo(a)',
  psiquiatra: 'Psiquiatra',
  terapeuta_holistico: 'Terapeuta Holístico(a)',
  educador_fisico: 'Educador(a) Físico(a)',
  massoterapeuta: 'Massoterapeuta',
  nutricionista: 'Nutricionista',
  assistente_social: 'Assistente Social',
  outro: 'Profissional de Saúde',
};

const MODALITY_LABELS = { online: 'Online', presencial: 'Presencial', hibrido: 'Híbrido' };

export default function ProfessionalPublicProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, [slug]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/professionals/${slug}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setProfile(res.data);
    } catch (err) {
      toast.error('Perfil não encontrado');
      navigate('/feed');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!profile) return null;

  const initials = (profile.display_name || '').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="professional-public-profile">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
        <ChevronLeft className="h-4 w-4 mr-1" /> Voltar
      </Button>

      {/* Header Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <Avatar className="w-24 h-24 shrink-0">
              <AvatarImage src={resolveImageUrl(profile.avatar_url)} alt={profile.display_name} />
              <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold" data-testid="professional-name">
                  {profile.display_name}
                </h1>
                {profile.verified && (
                  <Shield className="h-5 w-5 text-blue-600" />
                )}
              </div>
              
              {/* Profession */}
              <p className="text-lg text-muted-foreground mb-2" data-testid="professional-profession">
                {PROFESSION_LABELS[profile.profession] || profile.profession || 'Profissional'}
              </p>

              {/* Registry */}
              {profile.registry?.length > 0 && (
                <div className="flex items-center gap-2 mb-2" data-testid="professional-registry">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {profile.registry[0].type} {profile.registry[0].code}
                    {profile.registry[0].uf && ` / ${profile.registry[0].uf}`}
                  </span>
                </div>
              )}

              {/* Location */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                <MapPin className="h-4 w-4" />
                <span>{profile.city}, {profile.state}</span>
              </div>

              {/* Modalities */}
              <div className="flex flex-wrap gap-2">
                {profile.modalities?.map(m => (
                  <Badge key={m} variant="secondary">
                    {m === 'online' ? <Video className="h-3 w-3 mr-1" /> : <MapPin className="h-3 w-3 mr-1" />}
                    {MODALITY_LABELS[m] || m}
                  </Badge>
                ))}
                {profile.years_experience > 0 && (
                  <Badge variant="outline">
                    <Clock className="h-3 w-3 mr-1" />
                    {profile.years_experience} anos de experiência
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bio */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5" />
            Sobre
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm whitespace-pre-wrap" data-testid="professional-bio">{profile.bio}</p>
        </CardContent>
      </Card>

      {/* Specialties & Approaches */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <GraduationCap className="h-5 w-5" />
            Especialidades & Abordagens
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {profile.specialties?.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Especialidades</p>
              <div className="flex flex-wrap gap-2">
                {profile.specialties.map(s => (
                  <Badge key={s} variant="secondary">{s}</Badge>
                ))}
              </div>
            </div>
          )}
          
          {profile.approaches?.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Abordagens Terapêuticas</p>
              <div className="flex flex-wrap gap-2" data-testid="professional-approaches">
                {profile.approaches.map(a => (
                  <Badge key={a}>{a}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pricing */}
      {(profile.price_min || profile.price_max) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Briefcase className="h-5 w-5" />
              Valores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              Sessões a partir de{' '}
              <span className="font-bold text-lg">
                R$ {profile.price_min || profile.price_max}
              </span>
              {profile.price_max && profile.price_min && profile.price_max !== profile.price_min && (
                <span> a R$ {profile.price_max}</span>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Contact */}
      <Card>
        <CardContent className="pt-6">
          <Button className="w-full" size="lg" data-testid="contact-professional-button">
            <Phone className="h-5 w-5 mr-2" />
            Entrar em Contato
          </Button>
          {profile.links?.whatsapp && (
            <a
              href={`https://wa.me/${profile.links.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-2"
            >
              <Button variant="outline" className="w-full">
                WhatsApp
              </Button>
            </a>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
